import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import InboxesAPI from '../../api/inboxes';
import WhatsAppOnboardingAPI from '../../api/whatsAppOnboarding';
import WebChannel from '../../api/channel/webChannel';
import FBChannel from '../../api/channel/fbChannel';
import TwilioChannel from '../../api/channel/twilioChannel';
import { throwErrorMessage } from '../utils/api';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { ACCOUNT_EVENTS } from '../../helper/AnalyticsHelper/events';
import InboxMiddlewareAPI from '../../api/inboxMiddleware';

const buildInboxData = (inboxParams) => {
  const formData = new FormData();
  const { channel = {}, ...inboxProperties } = inboxParams;
  Object.keys(inboxProperties).forEach((key) => {
    formData.append(key, inboxProperties[key]);
  });
  const { selectedFeatureFlags, ...channelParams } = channel;
  // selectedFeatureFlags needs to be empty when creating a website channel
  if (selectedFeatureFlags) {
    if (selectedFeatureFlags.length) {
      selectedFeatureFlags.forEach((featureFlag) => {
        formData.append(`channel[selected_feature_flags][]`, featureFlag);
      });
    } else {
      formData.append('channel[selected_feature_flags][]', '');
    }
  }
  Object.keys(channelParams).forEach((key) => {
    formData.append(`channel[${key}]`, channel[key]);
  });
  return formData;
};

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isUpdatingIMAP: false,
    isUpdatingSMTP: false,
    isUpdatingSharedInbox: false,
    isOnboarding: false,
  },
  onboardingUrl: '',
  phoenixSuggestionConfigs: {},
  phoenixImproveConfigs: {},
};

export const getters = {
  getInboxes($state) {
    return $state.records;
  },
  getWhatsAppTemplates: ($state) => (inboxId) => {
    const [inbox] = $state.records.filter(
      (record) => record.id === Number(inboxId)
    );

    const {
      message_templates: whatsAppMessageTemplates,
      additional_attributes: additionalAttributes,
    } = inbox || {};

    const { message_templates: apiInboxMessageTemplates } =
      additionalAttributes || {};
    const messagesTemplates =
      whatsAppMessageTemplates || apiInboxMessageTemplates;

    // filtering out the whatsapp templates with media
    if (messagesTemplates instanceof Array) {
      return messagesTemplates.filter((template) => {
        return !template.components.some(
          (i) => i.format === 'IMAGE' || i.format === 'VIDEO'
        );
      });
    }
    return [];
  },
  getNewConversationInboxes($state) {
    return $state.records.filter((inbox) => {
      const { channel_type: channelType, phone_number: phoneNumber = '' } =
        inbox;

      const isEmailChannel = channelType === INBOX_TYPES.EMAIL;
      const isSmsChannel =
        channelType === INBOX_TYPES.TWILIO &&
        phoneNumber.startsWith('whatsapp');
      return isEmailChannel || isSmsChannel;
    });
  },
  getInbox: ($state) => (inboxId) => {
    const [inbox] = $state.records.filter(
      (record) => record.id === Number(inboxId)
    );
    return inbox || {};
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getWebsiteInboxes($state) {
    return $state.records.filter(
      (item) => item.channel_type === INBOX_TYPES.WEB
    );
  },
  getTwilioInboxes($state) {
    return $state.records.filter(
      (item) => item.channel_type === INBOX_TYPES.TWILIO
    );
  },
  getWhatsAppInboxes($state) {
    return $state.records.filter(
      (item) =>
        item.channel_type === INBOX_TYPES.WHATSAPP ||
        item?.additional_attributes?.apiInboxType === 'WhatsApp'
    );
  },
  getEmailInboxes($state) {
    return $state.records.filter(
      (item) => item.channel_type === INBOX_TYPES.EMAIL
    );
  },
  getSMSInboxes($state) {
    return $state.records.filter(
      (item) =>
        item.channel_type === INBOX_TYPES.SMS ||
        (item.channel_type === INBOX_TYPES.TWILIO && item.medium === 'sms')
    );
  },
  dialogFlowEnabledInboxes($state) {
    return $state.records.filter(
      (item) => item.channel_type !== INBOX_TYPES.EMAIL
    );
  },
  getOnboardingUrl($state) {
    return $state.onboardingUrl;
  },
  getIsOnboarding: ($state) => {
    return $state.uiFlags.isOnboarding;
  },
  getPhoenixSuggestionConfig: ($state) => (inboxId) => {
    if (!inboxId) {
      return undefined;
    }

    return $state.phoenixSuggestionConfigs[inboxId];
  },
  getPhoenixImproveConfig: ($state) => (accountId) => {
    if (!accountId) {
      return undefined;
    }

    return $state.phoenixImproveConfigs[accountId];
  },
};

const sendAnalyticsEvent = (channelType) => {
  AnalyticsHelper.track(ACCOUNT_EVENTS.ADDED_AN_INBOX, {
    channelType,
  });
};

export const actions = {
  revalidate: async ({ commit }, { newKey }) => {
    try {
      const isExistingKeyValid = await InboxesAPI.validateCacheKey(newKey);
      if (!isExistingKeyValid) {
        const response = await InboxesAPI.refetchAndCommit(newKey);
        commit(types.default.SET_INBOXES, response.data.payload);
      }
    } catch (error) {
      // Ignore error
    }
  },
  get: async ({ commit }, { useCache = true } = {}) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: true });
    try {
      const response = await InboxesAPI.get(useCache);
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
      commit(types.default.SET_INBOXES, response.data.payload);
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
    }
  },
  createChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      const { channel = {} } = params;
      sendAnalyticsEvent(channel.type);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(errorMessage);
    }
  },
  createWebsiteChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(buildInboxData(params));
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('website');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      return throwErrorMessage(error);
    }
  },
  createTwilioChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await TwilioChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('twilio');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  createFBChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await FBChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('facebook');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  updateInbox: async ({ commit }, { id, formData = true, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: true });
    try {
      const response = await InboxesAPI.update(
        id,
        formData ? buildInboxData(inboxParams) : inboxParams
      );
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
      throwErrorMessage(error);
    }
  },
  updateInboxIMAP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
      throwErrorMessage(error);
    }
  },
  updateInboxSMTP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
      throwErrorMessage(error);
    }
  },
  updateSharedInbox: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSharedInbox: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, {
        isUpdatingSharedInbox: false,
      });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, {
        isUpdatingSharedInbox: false,
      });
      throwErrorMessage(error);
    }
  },
  delete: async ({ commit, state, rootGetters }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: true });
    try {
      const inbox = state.records.find(
        (record) => record.id === Number(inboxId)
      );

      const isPhoenixWebchat =
        inbox?.additional_attributes?.apiInboxType === 'Webchat';
      const accountId =
        rootGetters?.getCurrentAccountId || inbox?.account_id || null;

      if (isPhoenixWebchat && accountId) {
        await InboxMiddlewareAPI.softDeleteInbox(inboxId, accountId);
        await InboxMiddlewareAPI.permanentlyDeleteInbox(inboxId, accountId);
      } else {
        await InboxesAPI.delete(inboxId);
      }
      commit(types.default.DELETE_INBOXES, inboxId);
      commit(types.default.DELETE_PHOENIX_SUGGESTION_CONFIG, inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
      throw error instanceof Error ? error : new Error(error);
    }
  },
  reauthorizeFacebookPage: async ({ commit }, params) => {
    try {
      const response = await FBChannel.reauthorizeFacebookPage(params);
      commit(types.default.EDIT_INBOXES, response.data);
      return response.data?.data ?? {};
    } catch (error) {
      throw new Error(error.message);
    }
  },
  deleteInboxAvatar: async (_, inboxId) => {
    try {
      await InboxesAPI.deleteInboxAvatar(inboxId);
    } catch (error) {
      throw new Error(error);
    }
  },
  getWAOnboardingStatus: async ({ commit }, { stopPolling, ...params }) => {
    try {
      const res = await WhatsAppOnboardingAPI.getWAOnboardingStatus(params);
      const { inboxId = null, isOnboardingComplete = false } = res?.data ?? {};
      const isOnboarding = !isOnboardingComplete && !stopPolling;

      commit(types.default.SET_INBOXES_UI_FLAG, { isOnboarding });
      if (!isOnboarding) {
        commit(types.default.SET_ONBOARDING_URL, '');
      }
      return inboxId;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isOnboarding: false });
      throw new Error(error.message);
    }
  },
  startWAOnboarding: async ({ commit }, params) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isOnboarding: true });
    try {
      const res = await WhatsAppOnboardingAPI.startWAOnboarding(params);
      const { onboardingUrl = '' } = res?.data ?? {};
      commit(types.default.SET_ONBOARDING_URL, onboardingUrl);
      if (!onboardingUrl) {
        commit(types.default.SET_INBOXES_UI_FLAG, { isOnboarding: false });
      }
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isOnboarding: false });
      throw new Error(error.message);
    }
  },
  fetchPhoenixSuggestionConfig: async ({ state, commit }, { inboxId, accountId }) => {
    if (!inboxId) {
      return undefined;
    }

    const cachedConfig = state.phoenixSuggestionConfigs[inboxId];

    // Only return cache if it has a valid value (not null)
    // This allows re-fetch when previous attempt returned null/failed
    if (cachedConfig !== undefined && cachedConfig !== null) {
      return cachedConfig;
    }

    try {
      const { data } = await InboxMiddlewareAPI.getInbox(inboxId, accountId);

      const suggestionsFlowId = data?.settings?.suggestionsFlowId || null;

      const config = {
        suggestionsFlowId,
      };

      commit(types.default.SET_PHOENIX_SUGGESTION_CONFIG, {
        inboxId,
        config,
      });

      return config;
    } catch (error) {
      commit(types.default.SET_PHOENIX_SUGGESTION_CONFIG, {
        inboxId,
        config: null,
      });
      return null;
    }
  },
  fetchPhoenixImproveConfig: async ({ state, commit }, accountId) => {
    if (!accountId) {
      return undefined;
    }

    const cachedConfig = state.phoenixImproveConfigs[accountId];

    // Only return cache if it has a valid value (not null)
    // This allows re-fetch when previous attempt returned null/failed
    if (cachedConfig !== undefined && cachedConfig !== null) {
      return cachedConfig;
    }

    try {
      const { data } = await InboxMiddlewareAPI.getConfiguration(accountId);

      const improveFlows = data?.configuration?.improveFlows || null;

      commit(types.default.SET_PHOENIX_IMPROVE_CONFIG, {
        accountId,
        config: improveFlows,
      });

      return improveFlows;
    } catch (error) {
      commit(types.default.SET_PHOENIX_IMPROVE_CONFIG, {
        accountId,
        config: null,
      });
      return null;
    }
  },
};

export const mutations = {
  [types.default.SET_INBOXES_UI_FLAG]($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },
  [types.default.SET_INBOXES]: MutationHelpers.set,
  [types.default.ADD_INBOXES]: MutationHelpers.create,
  [types.default.EDIT_INBOXES]: MutationHelpers.update,
  [types.default.DELETE_INBOXES]: MutationHelpers.destroy,
  [types.default.SET_ONBOARDING_URL]($state, url) {
    $state.onboardingUrl = url;
  },
  [types.default.SET_PHOENIX_SUGGESTION_CONFIG]($state, { inboxId, config }) {
    $state.phoenixSuggestionConfigs = {
      ...$state.phoenixSuggestionConfigs,
      [inboxId]: config,
    };
  },
  [types.default.DELETE_PHOENIX_SUGGESTION_CONFIG]($state, inboxId) {
    const { [inboxId]: _config, ...rest } = $state.phoenixSuggestionConfigs;
    $state.phoenixSuggestionConfigs = rest;
  },
  [types.default.SET_PHOENIX_IMPROVE_CONFIG]($state, { accountId, config }) {
    $state.phoenixImproveConfigs = {
      ...$state.phoenixImproveConfigs,
      [accountId]: config,
    };
  },
  [types.default.DELETE_PHOENIX_IMPROVE_CONFIG]($state, accountId) {
    const { [accountId]: _config, ...rest } = $state.phoenixImproveConfigs;
    $state.phoenixImproveConfigs = rest;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
